import { createRoute, useLoaderData } from "@tanstack/react-router";
import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { categoryPageQueryOptions } from "~/app/api/pageData/categoryPageQuery";
import type { ProductListPageInitialData } from "~/app/api/pageData/productListPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createProductListPage } from "~/pages/ProductListPage/ProductListPage";

function CategoryRoute() {
  const result = useLoaderData({
    from: "/c/$categoryCode",
  }) as PageInitialData<ProductListPageInitialData>;
  const shellProps = useStorefrontShellProps();
  const data = result.data;

  // The former JSP-local `window.SCHA.meta.categoryNameEn` global (consumed by
  // schaefflerFormHelper) is now sourced from route body data instead of a JSP
  // script tag. Seed the legacy reader from the page-data body until the form
  // helper migrates off the window.SCHA.meta path.
  useEffect(() => {
    window.SCHA.meta = window.SCHA.meta || {};
    window.SCHA.meta.categoryNameEn = data?.categoryNameEn ?? "";
  }, [data?.categoryNameEn]);

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createProductListPage({
        categoryName: data?.categoryName ?? "",
        bannerRelevantConfigurators: data?.bannerRelevantConfigurators ?? undefined,
        pageAnchor: data?.pageAnchor,
        showQuantityInCategoryName: data?.showQuantityInCategoryName,
        recommendationPlacement: data?.recommendationPlacement,
      })}
    </StorefrontShellFrame>
  );
}

export async function loadCategoryPageData(
  context: { queryClient: QueryClient },
  categoryCode: string,
  location: { pathname: string; searchStr: string } = { pathname: `/c/${categoryCode}`, searchStr: "" },
) {
  const result = await context.queryClient.ensureQueryData(categoryPageQueryOptions(categoryCode, location.searchStr));

  return unwrapPageInitialData(result);
}

export const categoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/c/$categoryCode",
  loader: async ({ context, params, location }) => loadCategoryPageData(context, params.categoryCode, location),
  component: CategoryRoute,
});
